import React, { useState, useRef, useEffect } from 'react';
import { BellIcon, HeartIcon, StarIcon, UserPlusIcon, TrophyIcon, XIcon, CheckIcon, ChecksIcon, UsersThreeIcon, SpinnerIcon } from '@phosphor-icons/react';
import { useNotifications, Notification } from '../context/NotificationsContext';
import { useProject } from '../context/ProjectContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationIcon: React.FC<{ type: string }> = ({ type }) => {
    switch (type) {
        case 'like':
            return <HeartIcon className="w-4 h-4 text-red-400 fill-red-400" />;
        case 'favorite':
            return <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" />;
        case 'follow':
            return <UserPlusIcon className="w-4 h-4 text-indigo-400" />;
        case 'challenge_new':
        case 'challenge_winner':
        case 'challenge_vote':
            return <TrophyIcon className="w-4 h-4 text-amber-400" />;
        case 'project_invite':
            return <UsersThreeIcon className="w-4 h-4 text-emerald-400" />;
        case 'project_join_request':
            return <UserPlusIcon className="w-4 h-4 text-amber-400" />;
        default:
            return <BellIcon className="w-4 h-4 text-zinc-400" />;
    }
};

const getNotificationMessage = (notification: Notification): string => {
    const actorName = notification.actor?.username || 'Someone';

    switch (notification.type) {
        case 'like':
            return `${actorName} liked your image`;
        case 'favorite':
            return `${actorName} favorited your image`;
        case 'follow':
            return `${actorName} subscribed to you`;
        case 'challenge_new':
            return 'A new weekly challenge is available!';
        case 'challenge_winner':
            return 'Congratulations! You won the weekly challenge!';
        case 'challenge_vote':
            return `${actorName} voted for your submission`;
        case 'project_invite':
            return `${actorName} invited you to join "${notification.project?.title || 'a project'}"`;
        case 'project_join_request':
            return `${actorName} requested to join "${notification.project?.title || 'your project'}"`;
        default:
            return notification.message || 'New notification';
    }
};

interface NotificationsDropdownProps {
    className?: string;
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ className = '' }) => {
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        clearNotification,
        acceptProjectInvitation,
        declineProjectInvitation,
        approveJoinRequest,
        declineJoinRequest
    } = useNotifications();
    const { refreshJoinedProjects, refreshProjectMembers } = useProject();
    const [isOpen, setIsOpen] = useState(false);
    const [processingInvite, setProcessingInvite] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Handle accept project invitation
    const handleAcceptInvite = async (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();
        if (!notification.membership_id) return;

        setProcessingInvite(notification.id);
        const success = await acceptProjectInvitation(notification.id, notification.membership_id);
        if (success) {
            // Refresh joined projects list so it appears immediately without refresh
            await refreshJoinedProjects();
        }
        setProcessingInvite(null);
    };

    // Handle decline project invitation
    const handleDeclineInvite = async (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();
        if (!notification.membership_id) return;

        setProcessingInvite(notification.id);
        await declineProjectInvitation(notification.id, notification.membership_id);
        setProcessingInvite(null);
    };

    // Handle approve join request (for project owners)
    const handleApproveJoinRequest = async (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();
        if (!notification.actor_id || !notification.project_id) return;

        setProcessingInvite(notification.id);
        const success = await approveJoinRequest(notification.id, notification.actor_id, notification.project_id);
        if (success && refreshProjectMembers) {
            // Refresh project members list so the new member appears
            await refreshProjectMembers();
        }
        setProcessingInvite(null);
    };

    // Handle decline join request (for project owners)
    const handleDeclineJoinRequest = async (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();

        setProcessingInvite(notification.id);
        await declineJoinRequest(notification.id);
        setProcessingInvite(null);
    };

    // Calculate dropdown position when opened
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            // Position dropdown 8px below button, aligned to button's left edge
            // If dropdown would go off-screen to the right, align to button's right edge instead
            const dropdownWidth = 320; // 80 * 4 (w-80)
            const left = rect.left;
            const wouldOverflowRight = left + dropdownWidth > window.innerWidth;

            setDropdownPosition({
                top: rect.bottom + 8,
                left: wouldOverflowRight ? rect.right - dropdownWidth : rect.left
            });
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }
        // TODO: Navigate to relevant page based on notification type
    };

    return (
        <div className={`relative ${className}`}>
            {/* Bell Button */}
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-zinc-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title="Notifications"
            >
                <BellIcon className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-lg shadow-red-500/30">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    className="fixed w-80 max-h-[480px] bg-onyx border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[9999] animate-in fade-in slide-in-from-top-2 duration-200"
                    style={{
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`
                    }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-zinc-950">
                        <h3 className="text-sm font-bold text-white">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
                            >
                                <ChecksIcon className="w-3.5 h-3.5" />
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="overflow-y-auto max-h-[360px] scrollbar-hide">
                        {loading && notifications.length === 0 ? (
                            <div className="py-12 text-center text-zinc-500 text-sm">
                                <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                Loading...
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="py-12 text-center">
                                <BellIcon className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                                <p className="text-zinc-500 text-sm">No notifications yet</p>
                                <p className="text-zinc-600 text-xs mt-1">We'll let you know when something happens</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`group flex gap-3 p-4 cursor-pointer transition-colors hover:bg-white/5 ${!notification.is_read ? 'bg-indigo-500/5' : ''
                                            }`}
                                    >
                                        {/* Actor Avatar */}
                                        <div className="relative flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/10 overflow-hidden">
                                                {notification.actor?.avatar_url ? (
                                                    <img src={notification.actor.avatar_url} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <NotificationIcon type={notification.type} />
                                                    </div>
                                                )}
                                            </div>
                                            {/* Type Badge */}
                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-onyx border border-white/10 flex items-center justify-center">
                                                <NotificationIcon type={notification.type} />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm leading-snug ${notification.is_read ? 'text-zinc-400' : 'text-white'}`}>
                                                {getNotificationMessage(notification)}
                                            </p>
                                            <p className="text-[10px] text-zinc-600 mt-1 font-mono">
                                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                            </p>

                                            {/* Project Invite Actions */}
                                            {notification.type === 'project_invite' && notification.membership_id && (
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => handleAcceptInvite(e, notification)}
                                                        disabled={processingInvite === notification.id}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        {processingInvite === notification.id ? (
                                                            <SpinnerIcon className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <CheckIcon className="w-3 h-3" />
                                                        )}
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleDeclineInvite(e, notification)}
                                                        disabled={processingInvite === notification.id}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-400 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        <XIcon className="w-3 h-3" />
                                                        Decline
                                                    </button>
                                                </div>
                                            )}

                                            {/* Project Join Request Actions (for project owners) */}
                                            {notification.type === 'project_join_request' && notification.actor_id && notification.project_id && (
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => handleApproveJoinRequest(e, notification)}
                                                        disabled={processingInvite === notification.id}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        {processingInvite === notification.id ? (
                                                            <SpinnerIcon className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <CheckIcon className="w-3 h-3" />
                                                        )}
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleDeclineJoinRequest(e, notification)}
                                                        disabled={processingInvite === notification.id}
                                                        className="flex items-center gap-1 px-2.5 py-1 bg-zinc-700/50 hover:bg-zinc-700 text-zinc-400 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        <XIcon className="w-3 h-3" />
                                                        Decline
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Image Preview (if applicable) */}
                                        {notification.generation?.img && (
                                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-white/5">
                                                <img src={notification.generation.img} className="w-full h-full object-cover" />
                                            </div>
                                        )}

                                        {/* Unread Indicator / Clear Button */}
                                        <div className="flex-shrink-0 flex items-center">
                                            {!notification.is_read ? (
                                                <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-lg shadow-indigo-500/50" />
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clearNotification(notification.id);
                                                    }}
                                                    className="p-1 text-zinc-600 hover:text-zinc-400 opacity-0 group-hover:opacity-100 transition-all"
                                                >
                                                    <XIcon className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="px-4 py-2.5 border-t border-white/5 bg-zinc-950">
                            <button className="w-full text-xs text-zinc-400 hover:text-white transition-colors text-center">
                                View all notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
